/** @jsx jsx */
import React, { useContext, useEffect, useState } from 'react'
import { Flex, Box, Text, jsx } from 'theme-ui'

import strings from './strings.json'
import moment from 'moment-timezone'
import formatPrice from '../../utils/formatPrice'

import LineItem from './LineItem'

import Body from '../shared/Typography/Body/Body'

import { ShopifyContext } from '../Layout'
import chevronRight from '../../icons/chevronRight.svg'
import { PaymentLogos } from '../footer/BottomMiscFooter'
import Timer from '../shared/Timer/Timer'
import StyledHeading from '../shared/Typography/Heading/Heading'
import { BgImg } from '../shared/BgImage'
import {
  getConvertedPrices,
  getActualShopifyId,
  getEncodedShopifyId,
} from '../../hooks/useShopifyFunctions'
import { CountryContext } from '../../gatsby/wrap-wroot-element'
import CheckoutButton from './CheckoutButton'
import { DiscountText } from '../shared/PromotionalAlert/Banner'
import useRebuy from '../../hooks/useRebuy'
import RebuyLineItem from '../cart/RebuyLineItem'

const { cartSubtotalLabel, cartHeader } = strings

function getConvertedSubtotal(lineItems) {
  return lineItems.reduce((acc, cur) => {
    const { convertedPrice, convertedCompareAtPrice } = getConvertedPrices(
      cur.customAttributes
    )

    return acc + cur.quantity * (convertedPrice || cur.variant.price)
  }, 0)
}

const trackConvertCart = (lineItems, currency) => {
  window.ccLayer = window.ccLayer || []
  const subtotalPrice = getConvertedSubtotal(lineItems)

  ccLayer.push({
    ccEvent: 'miniCartViewed',
    src: 'custom',
    items: lineItems
      ? lineItems.map(i =>
          // all the items present in the cart
          ({
            id: getActualShopifyId(i.variant.product.id, 'Product'), //id of prouct
            name: i.title + ' ' + i.variant.title, //name of product present in cart
            price: parseFloat(i.variant.price), //price of product
            currency: currency, //currency
            quantity: i.quantity, //quantity of product present in cart
            url: i.handle, //url of product page
            image: i.variant.image.src, //image url of the product
          })
        )
      : [],
    currency: currency, //currency
    total: subtotalPrice, //cart total including shipping, taxes
  })
}

function CartPage({
  toggleCart,

  ctaImage,
  ctaTitle,
  ctaSubTitle,
  ctaDescription,
  cartSubCta,
}) {
  const {
    checkout = {},
    updateItem,
    removeItem,
    notSupportedCountry,
    setCartHasItems,
    subtotalPrice,
  } = useContext(ShopifyContext)
  const geoState = useContext(CountryContext)

  const {
    fetchRebuyData: rebuyData,
    rebuyAddedToCart,
    setRebuyAddedToCart,
  } = useRebuy()
  const [recommendedData, setRecommendedData] = useState({})

  useEffect(() => {
    if (!geoState.loading) {
      trackConvertCart(checkout.lineItems, geoState.currency)
    }
  }, [geoState])

  useEffect(async () => {
    let shopifyIds = []

    cartHasLineItems &&
      checkout.lineItems.map(item => {
        shopifyIds.push(item.variant.product.id)
      })

    const fetchData = async () => {
      const response = await rebuyData(shopifyIds, 1)
      const data = await response.json()

      setRecommendedData(data)
    }

    fetchData()
  }, [])

  const { webUrl } = checkout
  const currency = !notSupportedCountry && geoState.currency

  const lineItemsPrice =
    checkout.loaded && getConvertedSubtotal(checkout.lineItems)

  const displaySubtotalPrice = formatPrice(
    Number(lineItemsPrice),
    undefined,
    currency
  )

  const hasDiscounts = checkout && checkout.hasDiscount

  async function decreaseProductAmount({ id, quantity, tryNow }) {
    if (quantity === 1) {
      try {
        await removeItem(id, setCartHasItems)
      } catch (error) {
        console.error(error)
      }
    } else {
      try {
        await updateItem({ id, quantity: quantity - 1, tryNow })
      } catch (error) {
        console.error(error)
      }
    }
  }

  async function increaseProductAmount({ id, quantity, tryNow }) {
    try {
      await updateItem({ id, quantity: quantity + 1, tryNow })
    } catch (error) {
      console.error(error)
    }
  }

  const buttonEnabled = checkout.loaded && checkout.lineItems.length > 0

  const todayAt6 = moment()
    .tz('America/Los_Angeles')
    .endOf('day')
    .subtract(6, 'hours')

  const tomorrowAt6 = moment()
    .tz('America/Los_Angeles')
    .add(1, 'day')
    .endOf('day')
    .subtract(6, 'hours')

  const countdownEndTime =
    todayAt6.diff(moment().tz('America/Los_Angeles'), 'seconds') < 0
      ? tomorrowAt6
      : todayAt6

  const cartHasLineItems =
    checkout.loaded && checkout.lineItems && checkout.lineItems.length > 0

  const webUrlWithReach = `${webUrl}&_rchcur=${currency || 'USD'}`

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        minHeight: [`100%`, null, `100vh`],
        maxHeight: [`100%`, null, `100vh`],
      }}
    >
      <Flex
        sx={{
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'stretch',
          textAlign: 'center',
          p: 3,
          pt: 0,
          bg: 'lightGray',
        }}
      >
        <Flex
          sx={{
            py: 15,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <img
            onClick={toggleCart}
            sx={{ width: '20px', height: '20px', cursor: 'pointer' }}
            src={chevronRight}
          />
        </Flex>
        {cartHasLineItems && ctaTitle && ctaImage && (
          <Flex
            sx={{ mb: 3, width: '100%', height: 'auto', border: 'default' }}
          >
            <Box sx={{ position: 'relative', width: '80px', height: 'auto' }}>
              <BgImg fluid={ctaImage.fluid} />
            </Box>
            <Box
              sx={{
                bg: 'white',
                width: '100%',
                p: '7px',
                py: '10px',
                textAlign: 'left',
              }}
            >
              <Body
                sx={{
                  fontSize: '8px',
                  lineHeight: '12px',
                  textTransform: 'uppercase',
                }}
                variant="caption"
              >
                {ctaSubTitle}
              </Body>
              <StyledHeading
                sx={{
                  mt: '3px',
                  fontSize: '11px',
                  lineHeight: '12px',
                  textTransform: 'uppercase',
                }}
                as="h6"
                variant="bold"
              >
                {ctaTitle}
              </StyledHeading>
              <Body
                variant="caption"
                sx={{
                  mt: '3px',

                  maxWidth: '260px',
                  lineHeight: '14px',
                  letterSpacing: '1px',
                  fontWeight: 500,
                }}
              >
                {ctaDescription}
              </Body>
            </Box>
          </Flex>
        )}
        <Box sx={{ mb: 2 }}>
          <CheckoutButton
            variantIds={
              buttonEnabled &&
              checkout.lineItems.map(lineItem => lineItem.variant.id)
            }
            url={webUrlWithReach}
            enabled={buttonEnabled}
          />
        </Box>
        <Body
          sx={{
            maxWidth: '400px',
            margin: 'auto',
            fontWeight: 'semiBold',
            color: 'text',
            textAlign: 'center',
          }}
          variant="caption"
        >
          <Box sx={{ my: '5px' }}>{cartSubCta}</Box>

          <Box sx={{ margin: 'auto', textAlign: 'center', mt: '5px' }}>
            <Timer
              countdownText={
                <Box sx={{ fontWeight: 'normal', display: 'inline-block' }}>
                  Order within:
                </Box>
              }
              countdownEndTime={countdownEndTime}
            />
          </Box>
        </Body>
      </Flex>

      <Box
        sx={{
          width: '100%',
          p: 10,
          overflowY: 'auto',
          height: '100%',
          pb: 70,
          flex: 1,
        }}
      >
        {checkout.loaded &&
          checkout.lineItems.map(lineItem => (
            <LineItem
              key={lineItem.id}
              lineItem={lineItem}
              decreaseProductAmount={decreaseProductAmount}
              increaseProductAmount={increaseProductAmount}
              removeItem={removeItem}
              setCartHasItems={setCartHasItems}
              currency={currency}
            />
          ))}
      </Box>

      <Box>
        {checkout.loaded &&
          !rebuyAddedToCart &&
          recommendedData.data &&
          recommendedData.data.map((item, key) => {
            return (
              <RebuyLineItem
                key={key}
                {...item}
                addedToCart={rebuyAddedToCart}
                setAddedToCart={setRebuyAddedToCart}
              />
            )
          })}
      </Box>

      <Box>
        {hasDiscounts && (
          <Flex
            sx={{
              bg: 'black',
              p: '10px 25px',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <DiscountText {...checkout.discounts[0]} />
          </Flex>
        )}
        <Flex
          sx={{
            bg: 'lightGray',
            p: '15px 25px',
            width: '100%',
            justifyContent: 'space-between',
            borderTop: 'default',
          }}
        >
          <Text fontSize={3}>{cartSubtotalLabel}</Text>
          <Text>{displaySubtotalPrice}</Text>
        </Flex>
        <Flex
          sx={{
            bg: 'lightGray',
            p: '15px 25px',
            width: '100%',
            justifyContent: 'space-between',
            borderTop: 'default',
          }}
        >
          <Text fontSize={3}>Shipping:</Text>
          <Text variant="semiBold">FREE</Text>
        </Flex>
        <Box sx={{ p: 10 }}>
          <PaymentLogos />
        </Box>
      </Box>
    </Flex>
  )
}

export default CartPage
